import QRCode from 'qrcode';
import { fmt } from './format';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function openDocument(html) {
  const popup = window.open('', '_blank', 'width=1080,height=900');
  if (!popup) return null;
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  return popup;
}

export function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function openReportPreview(title, subtitle, sections = []) {
  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { margin: 0; font-family: "Segoe UI", Arial, sans-serif; background: linear-gradient(135deg, #eff6ff, #f8fafc 45%, #ecfeff); color: #0f172a; padding: 28px; }
    .shell { max-width: 1100px; margin: 0 auto; display: grid; gap: 20px; }
    .hero { background: linear-gradient(135deg, #0f172a, #1d4ed8); color: #fff; border-radius: 28px; padding: 28px 32px; box-shadow: 0 26px 70px rgba(15,23,42,.22); }
    .hero h1 { margin: 0; font-size: 30px; letter-spacing: -.04em; }
    .hero p { margin: 10px 0 0; opacity: .82; }
    .section { background: rgba(255,255,255,.96); border: 1px solid rgba(148,163,184,.18); border-radius: 24px; padding: 22px 24px; box-shadow: 0 18px 40px rgba(15,23,42,.08); }
    .section h2 { margin: 0 0 14px; font-size: 18px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 13px; }
    th { text-transform: uppercase; letter-spacing: .08em; color: #64748b; font-size: 11px; }
    .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
    .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 18px; padding: 16px; }
    .label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: .08em; }
    .value { font-size: 24px; font-weight: 700; margin-top: 8px; letter-spacing: -.03em; }
    @media print { body { background: #fff; padding: 0; } .hero { box-shadow: none; } .section { box-shadow: none; border-radius: 0; page-break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="shell">
    <section class="hero">
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(subtitle)}</p>
    </section>
    ${sections.map((section) => section.type === 'cards' ? `
      <section class="section">
        <h2>${escapeHtml(section.title)}</h2>
        <div class="cards">
          ${section.items.map((item) => `<div class="card"><div class="label">${escapeHtml(item.label)}</div><div class="value">${escapeHtml(item.value)}</div></div>`).join('')}
        </div>
      </section>
    ` : `
      <section class="section">
        <h2>${escapeHtml(section.title)}</h2>
        <table>
          <thead><tr>${section.columns.map((column) => `<th>${escapeHtml(column)}</th>`).join('')}</tr></thead>
          <tbody>${section.rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')}</tbody>
        </table>
      </section>
    `).join('')}
  </div>
</body>
</html>`;

  return openDocument(html);
}

export async function openBarcodeSheetPreview({ title = 'Barcode Labels', items = [] }) {
  const labels = await Promise.all(items.map(async (item) => ({
    ...item,
    qr: await QRCode.toDataURL(String(item.barcode), { margin: 1, width: 180 }),
  })));

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { margin: 0; font-family: "Segoe UI", Arial, sans-serif; background: linear-gradient(135deg, #eff6ff, #f8fafc 45%, #fdf2f8); color: #0f172a; padding: 24px; }
    .shell { max-width: 1100px; margin: 0 auto; }
    .hero { background: linear-gradient(135deg, #111827, #be185d); color: #fff; border-radius: 28px; padding: 26px 30px; margin-bottom: 20px; }
    .hero h1 { margin: 0; font-size: 30px; letter-spacing: -.04em; }
    .hero p { margin: 8px 0 0; opacity: .82; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 18px; }
    .label { background: #fff; border-radius: 24px; padding: 18px; border: 1px solid rgba(148,163,184,.18); box-shadow: 0 18px 40px rgba(15,23,42,.10); page-break-inside: avoid; }
    .sku { font-size: 11px; text-transform: uppercase; letter-spacing: .12em; color: #6b7280; }
    .name { font-size: 16px; font-weight: 700; margin-top: 8px; line-height: 1.3; }
    .meta { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; gap: 10px; }
    .price { font-size: 18px; font-weight: 700; color: #1d4ed8; }
    .code { font-family: "Courier New", monospace; margin-top: 10px; font-size: 14px; letter-spacing: .12em; word-break: break-word; }
    .qr { margin-top: 14px; display: flex; justify-content: center; background: #f8fafc; border-radius: 18px; padding: 12px; }
    .qr img { width: 160px; height: 160px; }
    @media print { body { background: #fff; padding: 0; } .hero { box-shadow: none; } .label { box-shadow: none; border-radius: 0; } }
  </style>
</head>
<body>
  <div class="shell">
    <section class="hero">
      <h1>${escapeHtml(title)}</h1>
      <p>Use the browser print dialog to print label sheets or save them as PDF.</p>
    </section>
    <section class="grid">
      ${labels.map((label) => `
        <article class="label">
          <div class="sku">Barcode label</div>
          <div class="name">${escapeHtml(label.name)}</div>
          <div class="meta">
            <div class="price">${escapeHtml(label.price)}</div>
            <div>${escapeHtml(label.unit || '')}</div>
          </div>
          <div class="code">${escapeHtml(label.barcode)}</div>
          <div class="qr"><img src="${label.qr}" alt="QR code for ${escapeHtml(label.name)}" /></div>
        </article>
      `).join('')}
    </section>
  </div>
</body>
</html>`;

  return openDocument(html);
}
